/**
 * repair-booking.js — 维修/预约四步向导
 *
 * 整体流程：
 *   Step 1  选择预约类型（presales_demo / unboxing / troubleshooting）
 *   Step 2  日历选日期 + 时段（「今天」、7 天窗口、中午 12 点提前量
 *           都按该区域的官方时区。美国固定为美东 America/New_York，
 *           不使用用户浏览器所在地的时间）
 *   Step 3  按类型展示不同表单，填写后提交
 *   Step 4  提交成功页
 *
 * 配置来源：section 内 <script type="application/json" id="repair-booking-config-xxx">
 */
(function () {
  'use strict';

  /** 各区域默认 IANA 时区（block 未配置 timezone 时使用）。美国统一美东。 */
  var DEFAULT_TIMEZONE = {
    US: 'America/New_York',
    CA: 'America/Toronto',
    UK: 'Europe/London',
    AU: 'Australia/Sydney',
    EU: 'Europe/Berlin',
    JP: 'Asia/Tokyo'
  };

  // ─── 配置读取与数据预处理 ───────────────────────────────────────

  /** 从 Liquid 输出的 JSON 脚本标签读取 section 配置 */
  function parseConfig(sectionId) {
    var el = document.getElementById('repair-booking-config-' + sectionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[RepairBooking] Invalid config JSON', e);
      return null;
    }
  }

  /**
   * 把运营填的多行文本拆成数组（支持换行、逗号分隔）
   * 用于解析 available_dates、time_slots 等配置
   */
  function normalizeLineList(items) {
    var result = [];
    (items || []).forEach(function (item) {
      String(item).split(/[\r\n,]+/).forEach(function (part) {
        var trimmed = part.trim();
        if (trimmed && result.indexOf(trimmed) === -1) result.push(trimmed);
      });
    });
    return result.sort();
  }

  /**
   * 合并所有 region_schedule block 为 { US: {...}, CA: {...} } 结构
   * 同一 region 多个 block 会合并 dates / timeSlots
   */
  function mergeRegionConfig(regions, defaultTimeSlotsRaw) {
    var map = {};
    var fallbackSlots = normalizeLineList([defaultTimeSlotsRaw || '']);

    regions.forEach(function (item) {
      if (!item.region) return;
      var code = item.region.toUpperCase();
      if (!map[code]) {
        map[code] = {
          region: code,
          timezone: item.timezone || DEFAULT_TIMEZONE[code] || 'UTC',
          label: item.label || code,
          dates: [],
          timeSlots: [],
          weeklySessionLimit: 0
        };
      }
      if (item.timezone) map[code].timezone = item.timezone;
      if (code === 'US' && (!map[code].timezone || map[code].timezone === 'America/Los_Angeles')) {
        map[code].timezone = 'America/New_York';
      }
      if (item.label) map[code].label = item.label;
      var weeklyLimit = parseInt(item.weeklySessionLimit, 10);
      if (isFinite(weeklyLimit) && weeklyLimit >= 0) {
        map[code].weeklySessionLimit = weeklyLimit;
      }

      var dateSources = [];
      if (item.datesRaw) dateSources.push(item.datesRaw);
      normalizeLineList(dateSources).forEach(function (d) {
        if (map[code].dates.indexOf(d) === -1) map[code].dates.push(d);
      });

      var slotSources = [];
      if (item.timeSlotsRaw) slotSources.push(item.timeSlotsRaw);
      normalizeLineList(slotSources).forEach(function (t) {
        if (map[code].timeSlots.indexOf(t) === -1) map[code].timeSlots.push(t);
      });
    });

    Object.keys(map).forEach(function (code) {
      map[code].dates.sort();
      if (!map[code].timeSlots.length && fallbackSlots.length) {
        map[code].timeSlots = fallbackSlots.slice();
      }
      map[code].timeSlots.sort();
    });
    return map;
  }

  // ─── 日期 / 时间工具函数 ─────────────────────────────────────────

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /** 统一日期格式 YYYY-MM-DD，便于字符串比较 */
  function formatDateKey(year, month, day) {
    return year + '-' + pad(month + 1) + '-' + pad(day);
  }

  function parseDateKey(key) {
    var parts = key.split('-');
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10) - 1,
      day: parseInt(parts[2], 10)
    };
  }

  function addDaysToDateKey(dateKey, days) {
    var p = parseDateKey(dateKey);
    var date = new Date(p.year, p.month, p.day);
    date.setDate(date.getDate() + days);
    return formatDateKey(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /** 自然周起始日（周一）YYYY-MM-DD */
  function getIsoWeekStart(dateKey) {
    var p = parseDateKey(dateKey);
    var date = new Date(p.year, p.month, p.day);
    var day = date.getDay();
    var isoDow = day === 0 ? 7 : day;
    date.setDate(date.getDate() - (isoDow - 1));
    return formatDateKey(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /** 预约所用时区的展示名。美国固定美东。 */
  function scheduleTimeLabel(timeZone) {
    var labels = {
      'America/New_York': 'US Eastern Time (ET)',
      'America/Chicago': 'US Central Time (CT)',
      'America/Los_Angeles': 'US Pacific Time (PT)',
      'America/Toronto': 'Toronto time',
      'Europe/London': 'UK time',
      'Europe/Berlin': 'Central European Time',
      'Australia/Sydney': 'Sydney time',
      'Asia/Tokyo': 'Japan time'
    };
    return labels[timeZone] || timeZone || 'US Eastern Time (ET)';
  }

  function scheduleTimeSuffix(timeZone) {
    if (timeZone === 'America/New_York') return ' ET';
    if (timeZone === 'America/Chicago') return ' CT';
    if (timeZone === 'America/Los_Angeles') return ' PT';
    return '';
  }

  /**
   * 取某个 IANA 时区下的「现在」：日期 YYYY-MM-DD 和 0–23 点。
   * 用来计算该区域的今天、预约窗口和中午 12 点提前量。
   */
  function getZonedNow(timeZone) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    var map = {};
    fmt.formatToParts(new Date()).forEach(function (part) {
      if (part.type !== 'literal') map[part.type] = part.value;
    });
    var hour = parseInt(map.hour, 10);
    if (!isFinite(hour) || hour === 24) hour = 0;
    return {
      dateKey: map.year + '-' + map.month + '-' + map.day,
      hour: hour
    };
  }

  function formatDisplayDate(dateKey, locale) {
    var p = parseDateKey(dateKey);
    return new Intl.DateTimeFormat(locale || 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(p.year, p.month, p.day, 12, 0, 0));
  }

  function formatShortDate(dateKey, locale) {
    var p = parseDateKey(dateKey);
    return new Intl.DateTimeFormat(locale || 'en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(new Date(p.year, p.month, p.day, 12, 0, 0));
  }

  function formatMonthLabel(year, month, locale) {
    return new Intl.DateTimeFormat(locale || 'en-US', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(year, month, 1));
  }

  function formatTimeDisplay(time, locale) {
    var parts = time.split(':');
    return new Intl.DateTimeFormat(locale || 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(new Date(2000, 0, 1, parseInt(parts[0], 10), parseInt(parts[1] || '0', 10)));
  }

  function normalizeTime(time) {
    var parts = (time || '').trim().split(':');
    if (!parts[0]) return '';
    return pad(parseInt(parts[0], 10)) + ':' + pad(parseInt(parts[1] || '0', 10));
  }

  // ─── 图片 / 视频上传（故障类型 Step 3 使用）────────────────────

  /**
   * 单个上传区块：photo 最多 5 张，video 最多 1 个（默认 40MB 上限）
   * 选文件后本地预览，提交时通过 appendMediaFiles 写入 FormData
   */
  function MediaUploadBlock(block, options) {
    options = options || {};
    this.block = block;
    this.type = block.dataset.mediaUpload;
    this.maxFiles = this.type === 'photo' ? 5 : 1;
    this.maxVideoBytes = (options.maxVideoSizeMb || 40) * 1024 * 1024;
    this.videoSizeError = options.videoSizeError || 'Video must be 40 MB or smaller.';
    this.files = [];
    this.objectUrls = [];
    this.input = block.querySelector('[data-media-input]');
    this.preview = block.querySelector('[data-media-preview]');
    this.addBtn = block.querySelector('[data-media-add]');
    this.errorEl = block.querySelector('[data-media-error]');
    var self = this;

    if (this.addBtn) {
      this.addBtn.addEventListener('click', function () {
        self.openPicker();
      });
    }

    if (this.input) {
      this.input.addEventListener('change', function () {
        self.handleFiles(self.input.files);
      });
    }

    this.render();
  }

  MediaUploadBlock.prototype.openPicker = function () {
    if (this.input) this.input.click();
  };

  MediaUploadBlock.prototype.revokeUrls = function () {
    this.objectUrls.forEach(function (url) {
      URL.revokeObjectURL(url);
    });
    this.objectUrls = [];
  };

  MediaUploadBlock.prototype.showError = function (message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    } else {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  };

  MediaUploadBlock.prototype.handleFiles = function (fileList) {
    var self = this;
    this.showError('');

    Array.prototype.forEach.call(fileList || [], function (file) {
      if (self.type === 'photo') {
        if (!file.type.startsWith('image/')) return;
        if (self.files.length >= self.maxFiles) return;
        self.files.push(file);
      } else {
        if (!file.type.startsWith('video/')) return;
        if (file.size > self.maxVideoBytes) {
          self.showError(self.videoSizeError);
          return;
        }
        self.files = [file];
      }
    });

    if (this.input) this.input.value = '';
    this.render();
  };

  MediaUploadBlock.prototype.removeFile = function (index) {
    if (index < 0 || index >= this.files.length) return;
    this.files.splice(index, 1);
    this.showError('');
    this.render();
  };

  MediaUploadBlock.prototype.render = function () {
    var self = this;
    if (!this.preview) return;

    this.revokeUrls();
    this.preview.innerHTML = '';

    this.files.forEach(function (file, index) {
      var url = URL.createObjectURL(file);
      self.objectUrls.push(url);

      var item = document.createElement('div');
      item.className = 'repair-booking__media-item';

      if (self.type === 'photo') {
        var img = document.createElement('img');
        img.src = url;
        img.alt = file.name;
        item.appendChild(img);
      } else {
        var video = document.createElement('video');
        video.src = url;
        video.controls = true;
        video.preload = 'metadata';
        item.appendChild(video);

        var name = document.createElement('span');
        name.className = 'repair-booking__media-name';
        name.textContent = file.name;
        item.appendChild(name);
      }

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'repair-booking__media-remove';
      remove.setAttribute('data-media-remove', '');
      remove.dataset.index = String(index);
      remove.setAttribute('aria-label', 'Remove');
      remove.innerHTML = '&times;';
      remove.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.removeFile(index);
      });
      item.appendChild(remove);

      self.preview.appendChild(item);
    });

    if (this.addBtn) {
      this.addBtn.hidden = this.files.length >= this.maxFiles;
    }
  };

  /** 初始化页面上所有 [data-media-upload] 区块 */
  function initMediaUploads(root, options) {
    root.querySelectorAll('[data-media-upload]').forEach(function (block) {
      block._mediaUpload = new MediaUploadBlock(block, options);
    });
  }

  /** 提交时把 photo_0..4、video_0 追加到 FormData */
  function appendMediaFiles(formData, activeVariant) {
    if (!activeVariant) return;
    activeVariant.querySelectorAll('[data-media-upload]').forEach(function (block) {
      var upload = block._mediaUpload;
      if (!upload || !upload.files.length) return;
      var prefix = upload.type === 'photo' ? 'photo_' : 'video_';
      upload.files.forEach(function (file, i) {
        formData.append(prefix + i, file);
      });
    });
  }

  // ─── 自定义下拉（移动端底部弹层 + 桌面端下拉）────────────────────

  function closeAllCustomSelects(root) {
    (root || document).querySelectorAll('.repair-booking__select-dropdown.is-open').forEach(function (dropdown) {
      dropdown.classList.remove('is-open');
      dropdown.hidden = true;
      var wrap = dropdown._selectWrap;
      if (wrap && dropdown.parentNode !== wrap) {
        wrap.appendChild(dropdown);
      }
      if (wrap) {
        var trigger = wrap.querySelector('.repair-booking__select-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    });
    document.body.classList.remove('repair-booking--select-open');
  }

  /** 将原生 select 增强为品牌色自定义下拉，保留原生字段用于校验与提交 */
  function initCustomSelects(root) {
    root.querySelectorAll('.repair-booking__field select').forEach(function (select) {
      if (select.closest('.repair-booking__select-wrap')) return;

      var field = select.closest('.repair-booking__field');
      var labelEl = field ? field.querySelector('label') : null;
      var labelText = labelEl ? labelEl.textContent.replace(/\*/g, '').trim() : '';

      var wrap = document.createElement('div');
      wrap.className = 'repair-booking__select-wrap';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'repair-booking__select-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      var valueEl = document.createElement('span');
      valueEl.className = 'repair-booking__select-value';

      var chevron = document.createElement('span');
      chevron.className = 'repair-booking__select-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none">' +
        '<path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';

      trigger.appendChild(valueEl);
      trigger.appendChild(chevron);

      var dropdown = document.createElement('div');
      dropdown.className = 'repair-booking__select-dropdown';
      dropdown.hidden = true;

      var backdrop = document.createElement('div');
      backdrop.className = 'repair-booking__select-backdrop';

      var panel = document.createElement('div');
      panel.className = 'repair-booking__select-panel';

      var sheetTitle = document.createElement('div');
      sheetTitle.className = 'repair-booking__select-sheet-title';
      sheetTitle.textContent = labelText;

      var list = document.createElement('ul');
      list.className = 'repair-booking__select-list';
      list.setAttribute('role', 'listbox');

      Array.from(select.options).forEach(function (opt) {
        var li = document.createElement('li');
        li.className = 'repair-booking__select-option';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.textContent = opt.textContent;
        if (opt.selected) li.classList.add('is-selected');
        if (opt.disabled) li.classList.add('is-disabled');
        list.appendChild(li);
      });

      panel.appendChild(sheetTitle);
      panel.appendChild(list);
      dropdown.appendChild(backdrop);
      dropdown.appendChild(panel);
      dropdown._selectWrap = wrap;

      select.parentNode.insertBefore(wrap, select);
      wrap.appendChild(select);
      wrap.appendChild(trigger);
      wrap.appendChild(dropdown);
      select.classList.add('repair-booking__select-native');

      function syncDisplay() {
        var opt = select.options[select.selectedIndex];
        valueEl.textContent = opt ? opt.textContent : '';
        valueEl.classList.toggle('is-placeholder', !select.value);
        list.querySelectorAll('.repair-booking__select-option').forEach(function (li) {
          li.classList.toggle('is-selected', li.dataset.value === select.value);
          li.setAttribute('aria-selected', li.dataset.value === select.value ? 'true' : 'false');
        });
      }

      function openDropdown() {
        closeAllCustomSelects(root);
        if (window.matchMedia('(max-width: 749px)').matches) {
          document.body.appendChild(dropdown);
        }
        dropdown.hidden = false;
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('repair-booking--select-open');
      }

      function closeDropdown() {
        dropdown.classList.remove('is-open');
        dropdown.hidden = true;
        if (dropdown.parentNode !== wrap) {
          wrap.appendChild(dropdown);
        }
        trigger.setAttribute('aria-expanded', 'false');
        if (!root.querySelector('.repair-booking__select-dropdown.is-open')) {
          document.body.classList.remove('repair-booking--select-open');
        }
      }

      backdrop.addEventListener('touchmove', function (e) {
        e.preventDefault();
      }, { passive: false });

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (dropdown.classList.contains('is-open')) closeDropdown();
        else openDropdown();
      });

      backdrop.addEventListener('click', closeDropdown);

      list.addEventListener('click', function (e) {
        e.stopPropagation();
        var li = e.target.closest('.repair-booking__select-option');
        if (!li || li.classList.contains('is-disabled')) return;
        select.value = li.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncDisplay();
        wrap.classList.remove('repair-booking__select-wrap--invalid');
        closeDropdown();
      });

      select.addEventListener('change', syncDisplay);
      syncDisplay();
    });

    if (!root._customSelectBound) {
      root._customSelectBound = true;
      document.addEventListener('click', function (e) {
        if (
          !e.target.closest('.repair-booking__select-wrap') &&
          !e.target.closest('.repair-booking__select-dropdown')
        ) {
          closeAllCustomSelects(root);
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllCustomSelects(root);
      });
      root.querySelectorAll('input:not([type="file"]), textarea').forEach(function (input) {
        input.addEventListener('focus', function () {
          closeAllCustomSelects(root);
          if (window.matchMedia('(max-width: 749px)').matches) {
            window.requestAnimationFrame(function () {
              input.scrollIntoView({ block: 'center', behavior: 'smooth' });
            });
          }
        });
      });
    }
  }

  // ─── 主向导类 RepairBookingWizard ───────────────────────────────

  /**
   * 四步预约向导入口
   * @param {HTMLElement} root - .repair-booking 根节点
   */
  function RepairBookingWizard(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.config = parseConfig(this.sectionId);
    if (!this.config) return;

    this.texts = this.config.texts || {};
    this.bookingTypes = this.config.bookingTypes || {};
    this.currentStep = 1;
    this.selectedType = null;
    this.calendarInited = false;
    this.isSubmitting = false;

    this.initCalendarEngine();
    this.cacheDom();
    this.bindEvents();
    initMediaUploads(this.root, {
      maxVideoSizeMb: this.config.maxVideoSizeMb || 40,
      videoSizeError: (this.texts.videoSizeError || 'Video must be 40 MB or smaller.')
    });
    initCustomSelects(this.root);
    this.renderTimezoneNote();
    this.goToStep(1);
  }

  /**
   * 初始化日历引擎：确定当前 region、开放日期集合、预约窗口
   * region 优先级：URL ?region= > 域名识别 > section default_region
   */
  RepairBookingWizard.prototype.initCalendarEngine = function () {
    var config = this.config;
    this.regionMap = mergeRegionConfig(config.regions || [], config.defaultTimeSlotsRaw || '');
    this.locale = config.locale || 'en-US';
    this.bookingWindowDays = Math.max(1, parseInt(config.bookingWindowDays, 10) || 7);

    this.region = (
      (new URLSearchParams(window.location.search).get('region') || '').toUpperCase() ||
      config.hostRegion ||
      config.defaultRegion ||
      'US'
    );

    if (!this.regionMap[this.region]) {
      var keys = Object.keys(this.regionMap);
      if (keys.length) this.region = keys[0];
    }

    this.regionData = this.regionMap[this.region] || {
      region: this.region,
      timezone: DEFAULT_TIMEZONE[this.region] || 'UTC',
      label: this.region,
      dates: [],
      timeSlots: normalizeLineList([config.defaultTimeSlotsRaw || '']),
      weeklySessionLimit: 0
    };

    this.configuredDatesSet = new Set(this.regionData.dates);
    this.selectedDate = null;
    this.selectedSlot = null;
    this.bookedSlotsCache = {};
    this.weekCounts = {};
    this.weekCountsCacheKey = '';
    this.pendingSlotError = '';
    this.slotsRequestId = 0;
    this.isCheckingSlot = false;
    this.refreshBookingWindow();

    var initial = this.getInitialMonth();
    this.viewYear = initial.year;
    this.viewMonth = initial.month;
  };

  /** 缓存 DOM 引用，避免重复 querySelector */
  RepairBookingWizard.prototype.cacheDom = function () {
    this.panels = this.root.querySelectorAll('[data-step-panel]');
    this.stepIndicators = this.root.querySelectorAll('[data-step-indicator]');
    this.stepCircles = this.root.querySelectorAll('[data-step-circle]');
    this.typeCards = this.root.querySelectorAll('[data-booking-type]');
    this.backBtn = this.root.querySelector('[data-action="back"]');
    this.continueBtn = this.root.querySelector('[data-action="continue"]');
    this.submitBtn = this.root.querySelector('[data-action="submit"]');
    this.formVariants = this.root.querySelectorAll('[data-form-variant]');
    this.detailsForm = this.root.querySelector('[data-details-form]');
    this.formError = this.root.querySelector('[data-form-error]');
    this.summaryType = this.root.querySelector('[data-summary-type]');
    this.summaryDate = this.root.querySelector('[data-summary-date]');
    this.summaryTime = this.root.querySelector('[data-summary-time]');

    this.monthLabel = this.root.querySelector('[data-month-label]');
    this.calendarDays = this.root.querySelector('[data-calendar-days]');
    this.prevBtn = this.root.querySelector('[data-action="prev-month"]');
    this.nextBtn = this.root.querySelector('[data-action="next-month"]');
    this.dateLabel = this.root.querySelector('[data-selected-date-label]');
    this.timezoneNote = this.root.querySelector('[data-timezone-note]');
    this.slotsList = this.root.querySelector('[data-slots-list]');
    this.slotsEmpty = this.root.querySelector('[data-slots-empty]');
    this.slotsLoading = this.root.querySelector('[data-slots-loading]');
    this.slotError = this.root.querySelector('[data-slot-error]');
  };

  /** 绑定类型卡片、导航按钮、日历翻页等事件 */
  RepairBookingWizard.prototype.bindEvents = function () {
    var self = this;

    this.typeCards.forEach(function (card) {
      card.addEventListener('click', function () {
        self.selectType(card.dataset.bookingType);
      });
    });

    if (this.backBtn) {
      this.backBtn.addEventListener('click', function () {
        if (self.currentStep > 1) self.goToStep(self.currentStep - 1);
      });
    }

    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', function () {
        self.handleContinue();
      });
    }

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function () {
        self.handleSubmit();
      });
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.changeMonth(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.changeMonth(1); });
  };

  /** Step 1：记录选中的预约类型，高亮对应卡片 */
  RepairBookingWizard.prototype.selectType = function (type) {
    this.selectedType = type;
    this.typeCards.forEach(function (card) {
      card.classList.toggle('repair-booking__type-card--selected', card.dataset.bookingType === type);
    });
    this.updateNavButtons();
  };

  /**
   * 切换步骤：显示对应 panel、更新 stepper 圆圈/对勾、触发 Step2/3 初始化
   */
  RepairBookingWizard.prototype.goToStep = function (step) {
    this.currentStep = step;

    this.panels.forEach(function (panel) {
      var panelStep = parseInt(panel.dataset.stepPanel, 10);
      panel.hidden = panelStep !== step;
      panel.classList.toggle('repair-booking__panel--active', panelStep === step);
    });

    this.stepIndicators.forEach(function (indicator) {
      var n = parseInt(indicator.dataset.stepIndicator, 10);
      indicator.classList.remove('repair-booking__step--active', 'repair-booking__step--done');
      if (n === step) indicator.classList.add('repair-booking__step--active');
      else if (n < step) indicator.classList.add('repair-booking__step--done');
    });

    this.stepCircles.forEach(function (circle) {
      var n = parseInt(circle.dataset.stepCircle, 10);
      if (n < step) {
        circle.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      } else {
        circle.textContent = String(n);
      }
    });

    if (step === 2) this.initCalendarView();
    if (step === 3) this.showDetailsForm();

    this.updateNavButtons();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * 根据当前步骤控制 Back / Continue / Submit 的显示与 disabled 状态
   * Step1 需选类型；Step2 需选日期+时段；Step4 隐藏全部按钮
   */
  RepairBookingWizard.prototype.updateNavButtons = function () {
    var step = this.currentStep;
    var canContinue = false;

    if (step === 1) canContinue = !!this.selectedType;
    if (step === 2) canContinue = !!(this.selectedDate && this.selectedSlot) && !this.isCheckingSlot;

    var nav = this.root.querySelector('[data-wizard-nav]');
    if (nav) nav.hidden = step >= 4;

    if (this.backBtn) {
      this.backBtn.hidden = step <= 1 || step >= 4;
      this.backBtn.disabled = this.isCheckingSlot;
      this.backBtn.textContent = this.texts.back || 'Back';
    }
    if (this.continueBtn) {
      this.continueBtn.hidden = step >= 3;
      this.continueBtn.disabled = !canContinue;
      this.continueBtn.textContent = this.isCheckingSlot
        ? (this.texts.slotsLoading || 'Checking availability...')
        : (this.texts.continue || 'Continue');
    }
    if (this.submitBtn) {
      this.submitBtn.hidden = step !== 3;
      this.submitBtn.disabled = this.isSubmitting;
      this.submitBtn.textContent = this.isSubmitting
        ? (this.texts.submitting || 'Submitting...')
        : (this.texts.submit || 'Submit');
    }
  };

  /** Continue 按钮：Step1→2；Step2 先复检时段占用再进 Step3 */
  RepairBookingWizard.prototype.handleContinue = function () {
    if (this.currentStep === 1 && this.selectedType) {
      this.goToStep(2);
      return;
    }

    if (this.currentStep !== 2 || !this.selectedDate || !this.selectedSlot || this.isCheckingSlot) {
      return;
    }

    var self = this;
    this.clearSlotError();
    this.isCheckingSlot = true;
    this.updateNavButtons();

    this.verifySlotAvailable(this.selectedDate, this.selectedSlot, true)
      .then(function (result) {
        self.isCheckingSlot = false;
        var available = result === true || (result && result.available);
        var reason = result && result.reason;
        if (available) {
          self.goToStep(3);
          return;
        }
        if (reason === 'week_full') {
          self.pendingSlotError = self.texts.weekFull || "This week's sessions are fully booked. Please choose a date next week.";
          self.selectedSlot = null;
          self.selectedDate = null;
          return self.fetchWeekCounts(true).then(function () {
            self.renderCalendar();
            self.autoSelectFirstDate();
            if (self.pendingSlotError) {
              self.showSlotError(self.pendingSlotError);
              self.pendingSlotError = '';
            }
            self.updateNavButtons();
          });
        }
        self.showSlotError(self.texts.slotTaken || 'This time slot was just booked. Please choose another time.');
        self.selectedSlot = null;
        return self.loadAndRenderSlots(self.selectedDate).then(function () {
          self.updateNavButtons();
        });
      })
      .catch(function () {
        self.isCheckingSlot = false;
        self.showSlotError(self.texts.slotCheckError || 'Unable to verify availability. Please try again.');
        self.updateNavButtons();
      });
  };

  RepairBookingWizard.prototype.getSupabaseUrl = function () {
    if (this.config.supabaseUrl) return String(this.config.supabaseUrl).replace(/\/$/, '');
    var submit = (this.config.submitApiUrl || '').trim();
    var match = submit.match(/^(https:\/\/[^/]+\.supabase\.co)/i);
    return match ? match[1] : '';
  };

  /**
   * @param {boolean} json JSON 请求
   * @param {boolean} forRest PostgREST / RPC：publishable key 需 apikey 与 Authorization 相同
   */
  RepairBookingWizard.prototype.getApiHeaders = function (json, forRest) {
    var headers = {};
    var anonKey = (this.config.supabaseAnonKey || '').trim();
    if (anonKey) {
      headers.apikey = anonKey;
      var isNewKey = /^sb_(publishable|secret)_/.test(anonKey);
      if (!isNewKey) {
        headers.Authorization = 'Bearer ' + anonKey;
      } else if (forRest) {
        // PostgREST：Bearer 与 apikey 相同时才转发到数据库（Edge Function 则不要带 Authorization）
        headers.Authorization = 'Bearer ' + anonKey;
      }
    }
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
  };

  RepairBookingWizard.prototype.clearSlotError = function () {
    if (this.slotError) {
      this.slotError.hidden = true;
      this.slotError.textContent = '';
    }
  };

  RepairBookingWizard.prototype.showSlotError = function (message) {
    if (!this.slotError) return;
    this.slotError.textContent = message || '';
    this.slotError.hidden = !message;
  };

  RepairBookingWizard.prototype.getWeeklyLimit = function () {
    var n = parseInt(this.regionData && this.regionData.weeklySessionLimit, 10);
    if (!isFinite(n) || n < 1) return 0;
    return Math.min(99, n);
  };

  RepairBookingWizard.prototype.isWeekFull = function (dateKey) {
    var limit = this.getWeeklyLimit();
    if (!limit || !dateKey) return false;
    var weekStart = getIsoWeekStart(dateKey);
    var count = this.weekCounts[weekStart] || 0;
    return count >= limit;
  };

  RepairBookingWizard.prototype.fetchWeekCounts = function (forceRefresh) {
    var self = this;
    var limit = this.getWeeklyLimit();
    if (!limit) {
      this.weekCounts = {};
      return Promise.resolve(this.weekCounts);
    }

    this.refreshBookingWindow();
    var from = getIsoWeekStart(this.todayKey);
    var last = this.windowEndKey;
    if (this.regionData.dates && this.regionData.dates.length) {
      last = this.regionData.dates[this.regionData.dates.length - 1];
    }
    var to = addDaysToDateKey(getIsoWeekStart(last), 6);
    var cacheKey = this.region + '|' + from + '|' + to;
    if (!forceRefresh && this.weekCountsCacheKey === cacheKey) {
      return Promise.resolve(this.weekCounts);
    }

    var fnUrl = (this.config.submitApiUrl || '').trim();
    if (!fnUrl) {
      this.weekCounts = {};
      return Promise.resolve(this.weekCounts);
    }

    var query = '?action=week-counts&region=' + encodeURIComponent(this.region || '') +
      '&from=' + encodeURIComponent(from) +
      '&to=' + encodeURIComponent(to);

    return fetch(fnUrl + query, {
      method: 'GET',
      headers: this.getApiHeaders(false, false)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        self.weekCounts = (data && data.weeks) || {};
        self.weekCountsCacheKey = cacheKey;
        return self.weekCounts;
      })
      .catch(function () {
        if (forceRefresh) self.weekCounts = self.weekCounts || {};
        return self.weekCounts;
      });
  };

  /** 拉取某日已占用时段，优先走 Edge Function（与提交同域同鉴权） */
  RepairBookingWizard.prototype.fetchBookedSlots = function (dateKey, forceRefresh) {
    var cacheKey = this.region + '|' + dateKey;
    if (!forceRefresh && this.bookedSlotsCache[cacheKey]) {
      return Promise.resolve(this.bookedSlotsCache[cacheKey]);
    }

    var fnUrl = (this.config.submitApiUrl || '').trim();
    var self = this;

    if (fnUrl) {
      var query = '?action=booked-slots&date=' + encodeURIComponent(dateKey) +
        '&region=' + encodeURIComponent(this.region || '');
      return fetch(fnUrl + query, {
        method: 'GET',
        headers: this.getApiHeaders(false, false)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var rows = (data && data.slots) || [];
          if (!Array.isArray(rows)) rows = [];
          var booked = rows.map(function (row) {
            return normalizeTime(typeof row === 'string' ? row : (row.time_slot || row));
          }).filter(Boolean);
          self.bookedSlotsCache[cacheKey] = booked;
          return booked;
        });
    }

    var url = this.getSupabaseUrl();
    var anonKey = (this.config.supabaseAnonKey || '').trim();
    if (!url || !anonKey) {
      self.bookedSlotsCache[cacheKey] = [];
      return Promise.resolve([]);
    }

    return fetch(url + '/rest/v1/rpc/get_booked_slots', {
      method: 'POST',
      headers: this.getApiHeaders(true, true),
      body: JSON.stringify({ p_date: dateKey, p_region: this.region || null })
    })
      .then(function (res) {
        if (res.status === 404) {
          self.bookedSlotsCache[cacheKey] = [];
          return [];
        }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (rows) {
        if (!Array.isArray(rows)) {
          self.bookedSlotsCache[cacheKey] = [];
          return [];
        }
        var booked = rows.map(function (row) {
          return normalizeTime(row.time_slot || row);
        }).filter(Boolean);
        self.bookedSlotsCache[cacheKey] = booked;
        return booked;
      });
  };

  /**
   * 复检单个时段是否仍可预约
   * @param {boolean} forceRefresh 点击 Continue 时强制绕过缓存
   */
  RepairBookingWizard.prototype.verifySlotAvailable = function (dateKey, timeSlot, forceRefresh) {
    var normalized = normalizeTime(timeSlot);
    var self = this;
    var fnUrl = (this.config.submitApiUrl || '').trim();

    if (fnUrl) {
      var query = '?action=check-slot&date=' + encodeURIComponent(dateKey) +
        '&time_slot=' + encodeURIComponent(normalized) +
        '&region=' + encodeURIComponent(this.region || '') +
        '&weekly_limit=' + encodeURIComponent(String(this.getWeeklyLimit()));
      return fetch(fnUrl + query, {
        method: 'GET',
        headers: this.getApiHeaders(false, false)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          return {
            available: !!(data && data.available),
            reason: (data && data.reason) || (data && data.available ? 'ok' : 'slot_taken')
          };
        })
        .catch(function () {
          return self.fetchBookedSlots(dateKey, !!forceRefresh).then(function (booked) {
            if (self.isWeekFull(dateKey)) {
              return { available: false, reason: 'week_full' };
            }
            return {
              available: booked.indexOf(normalized) === -1,
              reason: booked.indexOf(normalized) === -1 ? 'ok' : 'slot_taken'
            };
          }).catch(function () {
            return { available: true, reason: 'ok' };
          });
        });
    }

    var url = this.getSupabaseUrl();
    var anonKey = (this.config.supabaseAnonKey || '').trim();
    if (!url || !anonKey) return Promise.resolve({ available: true, reason: 'ok' });

    return fetch(url + '/rest/v1/rpc/is_slot_available', {
      method: 'POST',
      headers: this.getApiHeaders(true, true),
      body: JSON.stringify({
        p_date: dateKey,
        p_time_slot: normalized,
        p_region: this.region || null
      })
    })
      .then(function (res) {
        if (res.status === 404) return { available: true, reason: 'ok' };
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (available) {
        var ok = available === true || (available && available.available);
        return { available: !!ok, reason: ok ? 'ok' : 'slot_taken' };
      })
      .catch(function () {
        // RPC 临时失败时退回已占用列表判断
        return self.fetchBookedSlots(dateKey, !!forceRefresh).then(function (booked) {
          if (self.isWeekFull(dateKey)) {
            return { available: false, reason: 'week_full' };
          }
          return {
            available: booked.indexOf(normalized) === -1,
            reason: booked.indexOf(normalized) === -1 ? 'ok' : 'slot_taken'
          };
        }).catch(function () {
          return { available: true, reason: 'ok' };
        });
      });
  };

  /** 首次进入 Step2 时渲染日历并自动选中第一个可预约日期 */
  RepairBookingWizard.prototype.initCalendarView = function () {
    var self = this;
    this.fetchWeekCounts(true)
      .catch(function () { return; })
      .then(function () {
        if (!self.calendarInited) {
          self.renderCalendar();
          self.autoSelectFirstDate();
          self.calendarInited = true;
        } else {
          self.renderCalendar();
          if (self.selectedDate && !self.isDateSelectable(self.selectedDate)) {
            self.selectedDate = null;
            self.selectedSlot = null;
            self.autoSelectFirstDate();
          } else if (self.selectedDate) {
            self.loadAndRenderSlots(self.selectedDate);
          } else {
            self.autoSelectFirstDate();
          }
        }
        if (self.pendingSlotError) {
          self.showSlotError(self.pendingSlotError);
          self.pendingSlotError = '';
        }
        self.updateNavButtons();
      });
  };

  /**
   * Step3：按 bookingType 显示对应表单变体，填充顶部预约摘要
   * 注意：三套表单都在 DOM 中，只显示匹配 data-form-variant 的那套
   */
  RepairBookingWizard.prototype.showDetailsForm = function () {
    var self = this;
    this.formVariants.forEach(function (variant) {
      variant.hidden = variant.dataset.formVariant !== self.selectedType;
    });

    var typeTitle = (this.bookingTypes[this.selectedType] || {}).title || this.selectedType;
    if (this.summaryType) this.summaryType.textContent = typeTitle;
    if (this.summaryDate) {
      this.summaryDate.textContent = formatShortDate(this.selectedDate, this.locale);
    }
    if (this.summaryTime) {
      this.summaryTime.textContent = this.formatSlotLabel(this.selectedSlot);
    }
    if (this.formError) this.formError.hidden = true;
    this.updateNavButtons();
  };

  /** 技术支持：是否至少上传了 1 张图片或 1 个视频 */
  RepairBookingWizard.prototype.hasRequiredMedia = function (activeVariant) {
    var total = 0;
    activeVariant.querySelectorAll('[data-media-upload]').forEach(function (block) {
      var upload = block._mediaUpload;
      if (upload && upload.files && upload.files.length) total += upload.files.length;
    });
    return total > 0;
  };

  /**
   * 只校验当前可见的那套表单（hidden 的表单字段不参与）
   * 使用 setCustomValidity 输出英文提示，避免浏览器默认中文校验文案
   */
  RepairBookingWizard.prototype.validateActiveForm = function (activeVariant) {
    var valid = true;
    var self = this;
    var firstInvalid = null;
    var inputs = activeVariant.querySelectorAll('input, select, textarea');

    inputs.forEach(function (input) {
      input.setCustomValidity('');
      if (input.type === 'file') return;

      var value = (input.value || '').trim();

      if (input.hasAttribute('required')) {
        if (input.tagName === 'SELECT') {
          var selectWrap = input.closest('.repair-booking__select-wrap');
          if (!value) {
            input.setCustomValidity(self.texts.selectRequired || 'Please select an option.');
            if (selectWrap) selectWrap.classList.add('repair-booking__select-wrap--invalid');
            valid = false;
            if (!firstInvalid) firstInvalid = input;
          } else if (selectWrap) {
            selectWrap.classList.remove('repair-booking__select-wrap--invalid');
          }
        } else if (!value) {
          input.setCustomValidity(self.texts.fieldRequired || 'Please fill in this field.');
          valid = false;
          if (!firstInvalid) firstInvalid = input;
        }
      }

      if (input.type === 'email' && value) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          input.setCustomValidity(self.texts.emailInvalid || 'Please enter a valid email address.');
          valid = false;
          if (!firstInvalid) firstInvalid = input;
        }
      }
    });

    var mediaRequiredEl = activeVariant.querySelector('[data-media-required]');
    var mediaErrorEl = activeVariant.querySelector('[data-media-required-error]');
    if (mediaRequiredEl) {
      var hasMedia = this.hasRequiredMedia(activeVariant);
      if (mediaErrorEl) {
        mediaErrorEl.hidden = hasMedia;
        if (!hasMedia) {
          mediaErrorEl.textContent = this.texts.mediaRequired || 'Please upload at least one photo or video.';
        }
      }
      if (!hasMedia) valid = false;
    }

    if (firstInvalid) firstInvalid.reportValidity();
    return valid;
  };

  /**
   * 提交逻辑：
   * 1. 校验当前表单
   * 2. 组装 FormData（表单字段 + 预约信息 + 图片/视频）
   * 3. POST 到 submit_api_url；留空则直接进 Step4 成功页
   */
  RepairBookingWizard.prototype.handleSubmit = function () {
    if (this.isSubmitting) return;

    var activeVariant = this.root.querySelector('[data-form-variant]:not([hidden])');
    if (!activeVariant || !this.detailsForm) return;

    if (!this.validateActiveForm(activeVariant)) return;

    this.isSubmitting = true;
    this.updateNavButtons();
    if (this.formError) this.formError.hidden = true;

    var formData = new FormData();
    var fields = activeVariant.querySelectorAll('[name]');
    fields.forEach(function (field) {
      if (field.type === 'file') return;
      formData.append(field.name, field.value);
    });

    formData.append('bookingType', this.selectedType);
    formData.append('bookingTypeLabel', (this.bookingTypes[this.selectedType] || {}).title || '');
    formData.append('region', this.region);
    var scheduleTimezone = (this.regionData && this.regionData.timezone) || 'America/New_York';
    formData.append('timezone', scheduleTimezone);
    formData.append('regionTimezone', scheduleTimezone);
    formData.append('date', this.selectedDate);
    formData.append('timeSlot', this.selectedSlot);
    formData.append('weeklySessionLimit', String(this.getWeeklyLimit()));
    formData.append('submittedAt', new Date().toISOString());
    formData.append('pageUrl', window.location.href);

    appendMediaFiles(formData, activeVariant);

    var apiUrl = (this.config.submitApiUrl || '').trim();
    var anonKey = (this.config.supabaseAnonKey || '').trim();
    var self = this;

    if (!apiUrl) {
      this.isSubmitting = false;
      this.goToStep(4);
      return;
    }

    var headers = this.getApiHeaders(false, false);

    fetch(apiUrl, { method: 'POST', headers: headers, body: formData })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || data.ok === false) {
            var err = new Error((data && data.error) || ('HTTP ' + res.status));
            err.code = data && data.code;
            err.status = res.status;
            throw err;
          }
          return data;
        });
      })
      .then(function () {
        self.isSubmitting = false;
        // 提交成功后清掉该日缓存，避免同页再约看到旧可用性
        delete self.bookedSlotsCache[self.region + '|' + self.selectedDate];
        self.goToStep(4);
      })
      .catch(function (err) {
        self.isSubmitting = false;
        var weekFull = err && (
          err.code === 'WEEK_FULL' ||
          /week.?full|sessions are fully booked/i.test(err.message || '')
        );

        if (weekFull) {
          self.pendingSlotError = self.texts.weekFull || "This week's sessions are fully booked. Please choose a date next week.";
          self.selectedSlot = null;
          self.selectedDate = null;
          self.goToStep(2);
          return;
        }

        var slotTaken = err && (
          err.code === 'SLOT_TAKEN' ||
          err.status === 409 ||
          /already booked|slot.?taken|duplicate/i.test(err.message || '')
        );

        if (slotTaken) {
          self.selectedSlot = null;
          self.goToStep(2);
          self.showSlotError(self.texts.slotTaken || 'This time slot was just booked. Please choose another time.');
          self.loadAndRenderSlots(self.selectedDate);
          return;
        }

        if (self.formError) {
          self.formError.textContent = self.texts.submitError || 'Submit failed';
          self.formError.hidden = false;
        }
        self.updateNavButtons();
      });
  };

  // ─── 日历可选日期规则（「今天」= 区域官方时区，美国为美东）────────

  /** 刷新「今天」、最早可选日、窗口结束日。12 点前后按该时区的钟点，不看浏览器本地时间。 */
  RepairBookingWizard.prototype.refreshBookingWindow = function () {
    var tz = (this.regionData && this.regionData.timezone) || 'America/New_York';
    var now = getZonedNow(tz);
    this.todayKey = now.dateKey;
    this.windowEndKey = addDaysToDateKey(this.todayKey, this.bookingWindowDays - 1);
    var leadDays = now.hour < 12 ? 1 : 2;
    this.minBookableDateKey = addDaysToDateKey(this.todayKey, leadDays);
  };

  RepairBookingWizard.prototype.renderTimezoneNote = function () {
    if (!this.timezoneNote) return;
    var tz = (this.regionData && this.regionData.timezone) || 'America/New_York';
    this.timezoneNote.textContent = 'All times are ' + scheduleTimeLabel(tz) + '.';
  };

  RepairBookingWizard.prototype.formatSlotLabel = function (time) {
    var tz = (this.regionData && this.regionData.timezone) || 'America/New_York';
    return formatTimeDisplay(time, this.locale) + scheduleTimeSuffix(tz);
  };

  /** 日期是否在预约窗口内（从今天起 N 天内） */
  RepairBookingWizard.prototype.isInBookingWindow = function (dateKey) {
    return dateKey >= this.todayKey && dateKey <= this.windowEndKey;
  };

  /** 日期是否可点击：在预约窗口内 AND 满足 12 点提前量 AND 在开放日期列表中 AND 该自然周未约满 */
  RepairBookingWizard.prototype.isDateSelectable = function (dateKey) {
    return (
      this.isInBookingWindow(dateKey) &&
      dateKey >= this.minBookableDateKey &&
      this.configuredDatesSet.has(dateKey) &&
      !this.isWeekFull(dateKey)
    );
  };

  /** 返回当前可预约的日期列表 */
  RepairBookingWizard.prototype.getSelectableDates = function () {
    return this.regionData.dates.filter(function (d) {
      return this.isDateSelectable(d);
    }, this);
  };

  RepairBookingWizard.prototype.getInitialMonth = function () {
    // 默认打开第一个可预约日期所在的月份
    var selectable = this.getSelectableDates();
    if (selectable.length) {
      var p = parseDateKey(selectable[0]);
      return { year: p.year, month: p.month };
    }
    var pToday = parseDateKey(this.todayKey);
    return { year: pToday.year, month: pToday.month };
  };

  RepairBookingWizard.prototype.autoSelectFirstDate = function () {
    // 进入 Step2 时自动选中最近的可预约日，减少用户操作
    var first = this.getSelectableDates()[0];
    if (first) {
      this.selectDate(first);
      return;
    }
    if (this.dateLabel) {
      this.dateLabel.textContent = this.texts.noDatesInWindow || this.texts.slotsPlaceholder;
    }
    this.showSlotsState('empty');
    if (this.slotsEmpty) {
      this.slotsEmpty.textContent = this.texts.noDatesInWindow || this.texts.noSlotsText;
    }
    this.updateNavButtons();
  };

  // ─── 日历渲染与时段选择 ─────────────────────────────────────────

  RepairBookingWizard.prototype.changeMonth = function (delta) {
    this.viewMonth += delta;
    if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear += 1; }
    else if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear -= 1; }
    this.renderCalendar();
  };

  /**
   * 渲染当月日历格：
   * - 过去日期灰色不可点
   * - 可预约日期蓝色可点
   * - 在开放列表但超出窗口的日期：灰色 + hover 提示
   */
  RepairBookingWizard.prototype.renderCalendar = function () {
    if (!this.calendarDays || !this.monthLabel) return;

    this.refreshBookingWindow();
    if (this.selectedDate && !this.isDateSelectable(this.selectedDate)) {
      this.selectedDate = null;
      this.selectedSlot = null;
      this.clearSlotError();
      if (this.dateLabel) {
        this.dateLabel.textContent = this.texts.slotsPlaceholder || '';
      }
      this.showSlotsState('empty');
      this.updateNavButtons();
    }
    this.monthLabel.textContent = formatMonthLabel(this.viewYear, this.viewMonth, this.locale);
    this.calendarDays.innerHTML = '';

    var firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay();
    var daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    var self = this;

    for (var i = 0; i < firstDay; i++) {
      var empty = document.createElement('span');
      empty.className = 'repair-booking__day repair-booking__day--empty';
      this.calendarDays.appendChild(empty);
    }

    for (var day = 1; day <= daysInMonth; day++) {
      var key = formatDateKey(this.viewYear, this.viewMonth, day);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'repair-booking__day';
      btn.textContent = String(day);
      btn.dataset.date = key;

      if (key < this.todayKey) {
        btn.classList.add('repair-booking__day--past');
        btn.disabled = true;
      } else if (this.isDateSelectable(key)) {
        btn.classList.add('repair-booking__day--available');
        if (key === this.selectedDate) btn.classList.add('repair-booking__day--selected');
        btn.addEventListener('click', function () { self.selectDate(this.dataset.date); });
      } else {
        btn.disabled = true;
        if (this.configuredDatesSet.has(key) && this.isInBookingWindow(key) && this.isWeekFull(key)) {
          btn.classList.add('repair-booking__day--week-full');
          btn.title = this.texts.weekFull || "This week's sessions are fully booked. Please choose a date next week.";
        } else if (
          this.configuredDatesSet.has(key) &&
          this.isInBookingWindow(key) &&
          key < this.minBookableDateKey
        ) {
          btn.classList.add('repair-booking__day--lead-time');
          btn.title = this.texts.leadTimeHint ||
            ('Appointments must be booked in advance. Before noon ' +
              scheduleTimeLabel((this.regionData && this.regionData.timezone) || 'America/New_York') +
              ', the earliest date is tomorrow; after noon, the day after tomorrow.');
        } else if (this.configuredDatesSet.has(key) && !this.isInBookingWindow(key)) {
          btn.classList.add('repair-booking__day--outside-window');
          btn.title = this.texts.outsideWindowHint || '';
        }
      }

      this.calendarDays.appendChild(btn);
    }
  };

  RepairBookingWizard.prototype.selectDate = function (dateKey) {
    if (!this.isDateSelectable(dateKey)) return;

    this.selectedDate = dateKey;
    this.selectedSlot = null;
    this.clearSlotError();
    this.renderCalendar();

    if (this.dateLabel) {
      this.dateLabel.textContent = formatDisplayDate(dateKey, this.locale);
    }

    this.loadAndRenderSlots(dateKey);
    this.updateNavButtons();
  };

  /** 选中日期后拉取占用情况并渲染时段（已约满置灰） */
  RepairBookingWizard.prototype.loadAndRenderSlots = function (dateKey) {
    var configured = (this.regionData.timeSlots || []).map(function (time) {
      return normalizeTime(time);
    }).filter(Boolean);

    if (!configured.length) {
      this.renderSlots([]);
      return Promise.resolve();
    }

    var requestId = ++this.slotsRequestId;
    var self = this;
    this.showSlotsState('loading');

    return this.fetchBookedSlots(dateKey, true)
      .catch(function () {
        return [];
      })
      .then(function (booked) {
        if (requestId !== self.slotsRequestId || self.selectedDate !== dateKey) return;

        var bookedSet = {};
        (booked || []).forEach(function (t) { bookedSet[t] = true; });

        var weekFull = self.isWeekFull(dateKey);
        if (weekFull && self.selectedSlot) {
          self.selectedSlot = null;
        } else if (self.selectedSlot && bookedSet[self.selectedSlot]) {
          self.selectedSlot = null;
        }

        var slots = configured.map(function (time) {
          return {
            time: time,
            available: !weekFull && !bookedSet[time],
            reason: weekFull ? 'week_full' : (bookedSet[time] ? 'slot_taken' : 'ok')
          };
        });
        self.renderSlots(slots);
        self.updateNavButtons();
      });
  };

  RepairBookingWizard.prototype.renderSlots = function (slots) {
    if (!this.slotsList) return;
    this.slotsList.innerHTML = '';

    if (!slots.length) {
      this.showSlotsState('empty');
      if (this.slotsEmpty) this.slotsEmpty.textContent = this.texts.noSlotsText;
      return;
    }

    this.showSlotsState('list');
    var self = this;

    slots.forEach(function (slot) {
      var time = normalizeTime(slot.time);
      var available = slot.available !== false;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'repair-booking__slot';
      btn.dataset.time = time;
      btn.dataset.available = available ? '1' : '0';
      btn.textContent = self.formatSlotLabel(time);

      if (!available) {
        btn.classList.add('repair-booking__slot--unavailable');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.title = slot.reason === 'week_full'
          ? (self.texts.weekFull || "This week's sessions are fully booked. Please choose a date next week.")
          : (self.texts.slotTaken || 'This time slot is already booked');
      } else if (time === self.selectedSlot) {
        btn.classList.add('repair-booking__slot--selected');
        btn.innerHTML = self.getConfirmedHtml();
        btn.addEventListener('click', function () { self.selectSlot(time); });
      } else {
        btn.addEventListener('click', function () { self.selectSlot(time); });
      }

      self.slotsList.appendChild(btn);
    });
  };

  RepairBookingWizard.prototype.getConfirmedHtml = function () {
    return '<svg class="repair-booking__slot-check" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
      '<path d="M3.5 9.5L7 13L14.5 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' + (this.texts.confirmed || 'Confirmed');
  };

  /** 选中时段后更新 Continue 按钮状态（需 date + slot 同时存在） */
  RepairBookingWizard.prototype.selectSlot = function (time) {
    var target = this.slotsList && this.slotsList.querySelector('.repair-booking__slot[data-time="' + time + '"]');
    if (target && target.dataset.available === '0') return;

    this.selectedSlot = time;
    this.clearSlotError();
    var self = this;

    this.slotsList.querySelectorAll('.repair-booking__slot').forEach(function (btn) {
      var available = btn.dataset.available !== '0';
      btn.classList.remove('repair-booking__slot--selected');
      if (!available) return;
      if (btn.dataset.time === time) {
        btn.classList.add('repair-booking__slot--selected');
        btn.innerHTML = self.getConfirmedHtml();
      } else {
        btn.textContent = self.formatSlotLabel(btn.dataset.time);
      }
    });

    this.updateNavButtons();
  };

  RepairBookingWizard.prototype.showSlotsState = function (state) {
    if (this.slotsLoading) this.slotsLoading.hidden = state !== 'loading';
    if (this.slotsEmpty) this.slotsEmpty.hidden = state !== 'empty';
    if (this.slotsList) this.slotsList.hidden = state !== 'list';
  };

  RepairBookingWizard.prototype.getSelection = function () {
    if (!this.selectedDate || !this.selectedSlot) return null;
    return {
      bookingType: this.selectedType,
      region: this.region,
      timezone: (this.regionData && this.regionData.timezone) || 'America/New_York',
      date: this.selectedDate,
      timeSlot: this.selectedSlot
    };
  };

  // ─── 启动入口（页面加载 + 主题编辑器 section 热重载）────────────

  function initAll() {
    document.querySelectorAll('.repair-booking[data-section-id]').forEach(function (el) {
      el._repairBookingWizard = new RepairBookingWizard(el);
    });
  }

  window.RepairBookingWizard = RepairBookingWizard;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.repair-booking[data-section-id]');
    if (el) el._repairBookingWizard = new RepairBookingWizard(el);
  });
})();
